import {
  ExclamationCircleOutlined,
  HolderOutlined,
  MoreOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Button, Col, Dropdown, MenuProps, Row, Space, Typography } from 'antd';
import { FC, useCallback, useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { useProgramStepContext } from 'src/features/programsV2/contexts/ProgramStepContext';
import { useDropdownManager } from 'src/features/programsV2/hooks/useDropdownManager';
import { ContentStepForm } from 'src/features/programsV2/schemas/contentStepSchema';
import {
  createEmptyContentWithInstructor,
  createEmptyModuleWithInstructor,
} from 'src/features/programsV2/utils/formHelpers';
import { showConfirmModal } from 'src/features/programsV2/utils/modalHelpers';

import { ProgramSectionItem } from '../../ProgramSectionItem';
import { DragButton } from '../../ui/DragButton';
import { SortableItem } from '../../ui/SortableItem';
import { TransparentButton } from '../../ui/TransparentButton';
import ConfigureModule, { ConfigureModuleProps } from './ConfigureModule';
import { ProgramContents } from './ProgramContents';

const { Title, Text } = Typography;

export const ProgramModules: FC = () => {
  const {
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<ContentStepForm>();
  const { programApiInitialValues } = useProgramStepContext();

  const { fields, append, remove, insert, move } = useFieldArray({
    control,
    name: 'modules',
  });

  const EMPTY_MODULE_WITH_INSTRUCTOR = createEmptyModuleWithInstructor(
    programApiInitialValues?.initialStep.instructorIds[0] ?? 0
  );

  const EMPTY_CONTENT_WITH_INSTRUCTOR = createEmptyContentWithInstructor(
    programApiInitialValues?.initialStep.instructorIds[0] ?? 0
  );

  const {
    dropdownStates,
    toggleDropdown,
    updateDropdownStates,
    updateDropdownsOrder,
    openAllDropdowns,
  } = useDropdownManager();

  const [configureModuleProps, setConfigureModuleProps] = useState<
    Pick<ConfigureModuleProps, 'isOpen' | 'moduleIndex'>
  >({
    isOpen: false,
    moduleIndex: -1,
  });

  const openConfigureModule = (moduleIndex: number) => {
    setConfigureModuleProps({
      isOpen: true,
      moduleIndex,
    });
  };

  const closeConfigureModule = () => {
    setConfigureModuleProps({
      isOpen: false,
      moduleIndex: -1,
    });
  };

  const addEmptyModule = () => {
    append(EMPTY_MODULE_WITH_INSTRUCTOR);
  };

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (active.id !== over?.id) {
        const oldIndex = Number(active.id);
        const newIndex = Number(over?.id);

        if (oldIndex === 0 || newIndex === 0) {
          return;
        }

        move(oldIndex, newIndex);

        updateDropdownsOrder(oldIndex, newIndex);
      }
    },
    [updateDropdownsOrder, move]
  );

  const handleDuplicate = (index: number) => {
    const modules = getValues('modules');
    if (!modules) return;
    const moduleToDuplicate = modules[index];
    insert(index + 1, {
      ...moduleToDuplicate,
      title: `${moduleToDuplicate.title} (copy)`,
    });
  };

  const handleClear = (index: number) => {
    const modules = getValues('modules');
    if (!modules) return;
    const moduleTitle = modules[index].title;
    const clearedModules = modules.map((module, i) =>
      i === index
        ? { ...module, contents: [EMPTY_CONTENT_WITH_INSTRUCTOR] }
        : module
    );
    showConfirmModal({
      icon: <ExclamationCircleOutlined style={{ color: 'red' }} />,
      title: 'Limpar conteúdos',
      content: (
        <Space
          size={'small'}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Row>
            <Text>
              {moduleTitle
                ? `Deseja realmente limpar todos conteúdos do módulo "${moduleTitle}"? `
                : 'Deseja realmente limpar todos conteúdos deste módulo? '}
            </Text>
          </Row>
          <Row>
            <Text> Essa ação não poderá ser desfeita.</Text>
          </Row>
        </Space>
      ),
      cancelText: 'Cancelar',
      okText: 'Limpar Módulo',
      okButtonProps: {
        style: { backgroundColor: 'red', color: 'white', borderColor: 'red' },
      },
      okType: 'danger',
      onOk: () => {
        setValue('modules', clearedModules);
      },
    });
  };

  const handleRemove = (index: number) => {
    const modules = getValues('modules');
    if (!modules) return;
    const moduleTitle = modules[index].title;
    showConfirmModal({
      icon: <ExclamationCircleOutlined style={{ color: 'red' }} />,
      title: 'Excluir Módulo',
      content: (
        <Space
          size={'small'}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Row>
            <Text>
              {moduleTitle
                ? `Deseja realmente excluir o módulo "${moduleTitle}"? `
                : 'Deseja realmente excluir este módulo? '}
            </Text>
          </Row>
          <Row>
            <Text>Essa ação não poderá ser desfeita.</Text>
          </Row>
        </Space>
      ),
      cancelText: 'Cancelar',
      okText: 'Excluir Módulo',
      okButtonProps: {
        style: { backgroundColor: 'red', color: 'white', borderColor: 'red' },
      },
      okType: 'danger',
      onOk: () => {
        remove(index);
      },
    });
  };

  const handleConfigure = (index: number) => {
    openConfigureModule(index);
  };

  const dropdownItems: MenuProps['items'] = [
    {
      key: 'duplicate',
      label: 'Duplicar',
    },
    { key: 'clear', label: 'Limpar' },
    { key: 'remove', label: 'Excluir' },
    { key: 'configure', label: 'Configurar' },
  ];

  const actionHandlers: Record<string, (index: number) => void> = {
    duplicate: handleDuplicate,
    clear: handleClear,
    remove: handleRemove,
    configure: handleConfigure,
  };

  const handleDropdownAction = useCallback(
    (key: string, index: number) => {
      const action = actionHandlers[key];
      if (action) {
        action(index);
      }
    },
    [actionHandlers]
  );

  useEffect(() => {
    if (fields.length - 1 === 0) {
      updateDropdownStates({ 0: true });
    }
  }, [fields]);

  useEffect(() => {
    if (errors.modules && Array.isArray(errors.modules)) {
      const hasContentErrors = errors.modules.some(
        module => module?.contents && module.contents.length > 0
      );

      if (hasContentErrors) {
        openAllDropdowns();
      }
    }
  }, [errors.modules, openAllDropdowns]);

  useEffect(() => {
    fields.map((_, index) =>
      updateDropdownStates(prev => {
        if (fields.length - 1 === index) {
          return { ...prev, [index]: true };
        }
        return { ...prev, [index]: false };
      })
    );
  }, [fields.length]);

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <SortableContext
          items={fields.map((_, sortIdx) => String(sortIdx))}
          strategy={verticalListSortingStrategy}
        >
          <Title level={4}>Módulos</Title>
          {fields.map((field, index) => {
            const isFirstModule = index === 0;

            return (
              <SortableItem
                key={field.id}
                sortId={String(index)}
                disabled={isFirstModule}
              >
                {(listeners, isDragging) => (
                  <ProgramSectionItem
                    key={field.id}
                    isDropdown
                    isOpen={!!dropdownStates[index]}
                    onToggleDropdown={() => toggleDropdown(index)}
                    isEditableTitle
                    editableTitleProps={{
                      control,
                      name: `modules.${index}.title`,
                      placeholder: 'Novo módulo',
                    }}
                    addonBefore={
                      isFirstModule ? null : (
                        <DragButton {...listeners} isDragging={isDragging}>
                          <HolderOutlined />
                        </DragButton>
                      )
                    }
                    addonAfter={
                      <Dropdown
                        menu={{
                          items: dropdownItems,
                          onClick: ({ key }) =>
                            handleDropdownAction(key, index),
                        }}
                        trigger={['click']}
                        placement='bottomRight'
                        overlayStyle={{ minWidth: '200px' }}
                      >
                        <TransparentButton
                          icon={<MoreOutlined />}
                          style={{
                            width: '30px',
                            height: '22px',
                          }}
                        />
                      </Dropdown>
                    }
                    style={{
                      margin: '0 0 16px 0',
                      border: isDragging ? '2px dashed #187DDC' : undefined,
                      borderRadius: '8px',
                    }}
                  >
                    <ProgramContents moduleIndex={index} />
                  </ProgramSectionItem>
                )}
              </SortableItem>
            );
          })}
        </SortableContext>

        <Row justify={'center'}>
          <Col>
            <Button
              type='link'
              icon={<PlusCircleOutlined />}
              onClick={addEmptyModule}
              style={{ marginTop: 16, marginInline: 'auto' }}
            >
              Adicionar Modulo
            </Button>
          </Col>
        </Row>

        {configureModuleProps.isOpen && (
          <ConfigureModule
            moduleIndex={configureModuleProps.moduleIndex}
            isOpen={configureModuleProps.isOpen}
            onClose={closeConfigureModule}
          />
        )}
      </div>
    </DndContext>
  );
};
